import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Categories and catalogue facets (tag counts, age groups, price range),
 * fetched once per language/category and shared by the header's category
 * row, the home page and the listing filters.
 */
const cache = new Map();

const load = (key, request) => {
  if (!cache.has(key)) {
    cache.set(key, request().catch((error) => {
      cache.delete(key);
      throw error;
    }));
  }
  return cache.get(key);
};

export const fetchCategories = (lang) => load(`categories:${lang}`, () =>
  api.get('/api/categories', { params: { lang } }).then((response) => response.data.categories || []));

export const fetchFacets = (categorySlug = '') => load(`facets:${categorySlug}`, () =>
  api.get('/api/products/facets', { params: categorySlug ? { categorySlug } : {} }).then((response) => response.data));

export const clearCatalogCache = () => cache.clear();

export function useCatalog(lang, categorySlug = '') {
  const [state, setState] = useState({ categories: [], facets: null, loaded: false });
  useEffect(() => {
    let active = true;
    Promise.all([fetchCategories(lang).catch(() => []), fetchFacets(categorySlug).catch(() => null)])
      .then(([categories, facets]) => { if (active) setState({ categories, facets, loaded: true }); });
    return () => { active = false; };
  }, [lang, categorySlug]);
  return state;
}

/** Label for a product tag: translated when known, otherwise tidied up. */
export const tagLabel = (t, tag) => t(`tags.${tag}`, { defaultValue: tag.replace(/[-_]+/g, ' ').replace(/^./, (c) => c.toUpperCase()) });

/** "3–5 vjeç" style label for an age group from the facets endpoint. */
export const ageGroupLabel = (t, group) => t('pdp.ageRange', { min: group.min, max: group.max, defaultValue: `${group.min}–${group.max} yrs` });

/** Listing URL for an age group (read by the listing's `age` parameter). */
export const ageGroupParam = (group) => `${group.min}-${group.max}`;
